import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API_BASE } from "../../components/AuthContext";
import {
  Inbox, FileSignature, Receipt, ArrowRight,
  CheckCircle2, Clock, AlertCircle, Sparkles,
} from "lucide-react";
import { usePortalAuth } from "../../components/PortalAuthContext";

// v68.14 — Portal dashboard, "Steel & Signal" refresh.
// Adds: greeting header w/ time-of-day, attention strip that surfaces the
// highest-priority open items, richer cards, and a soft accent hero.

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";
const fmtAmt = (n) => n != null ? `$${Number(n).toLocaleString()}` : "—";

function Pill({ tone = "subtle", children }) {
  const tones = {
    subtle: "bg-[var(--subtle)] text-[var(--ink-2)] border border-[var(--border)]",
    accent: "bg-[var(--accent)] text-[var(--paper)]",
    moss:   "bg-[var(--moss)] text-[var(--paper)]",
    ink:    "bg-[var(--ink)] text-[var(--paper)]",
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium tracking-wide ${tones[tone]}`}>{children}</span>;
}

function greeting() {
  const h = new Date().getHours();
  if (h < 5) return "Working late";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function AttentionStrip({ data }) {
  const items = [];
  const pendingFiles = (data.recent?.files || []).filter((f) => f.status !== "completed");
  const pendingSigs = (data.recent?.signatures || []).filter((s) => s.status !== "signed");
  const unpaidInvs = (data.recent?.invoices || []).filter((i) => i.status === "sent");
  if (pendingFiles.length) items.push({
    href: "/portal/files", icon: <Inbox size={14}/>, label: "documents to upload",
    count: pendingFiles.length, testid: "attn-files",
  });
  if (pendingSigs.length) items.push({
    href: "/portal/signatures", icon: <FileSignature size={14}/>, label: "signatures needed",
    count: pendingSigs.length, testid: "attn-sigs",
  });
  if (unpaidInvs.length) items.push({
    href: "/portal/invoices", icon: <Receipt size={14}/>, label: "invoices to pay",
    count: unpaidInvs.length, testid: "attn-invs",
  });
  if (items.length === 0) {
    return (
      <div className="rounded-md border border-[var(--border)] bg-[var(--subtle)] p-4 flex items-center gap-3 mb-8"
           data-testid="portal-attention-empty">
        <div className="w-8 h-8 rounded-full bg-[var(--moss)] flex items-center justify-center shrink-0">
          <CheckCircle2 size={16} className="text-[var(--paper)]"/>
        </div>
        <div>
          <div className="text-[14px] font-medium text-[var(--ink)]">You&apos;re all caught up</div>
          <div className="text-[12px] text-[var(--ink-2)]">Nothing needs your attention right now. We&apos;ll ping you the moment it does.</div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-[var(--accent)] p-4 mb-8 relative overflow-hidden"
         style={{ background: "rgba(185, 28, 28, 0.03)" }}
         data-testid="portal-attention-strip">
      <div className="flex items-center gap-2 mb-3">
        <AlertCircle size={14} className="text-[var(--accent)]"/>
        <span className="overline text-[10px] text-[var(--accent)]">Needs your attention</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {items.map((it) => (
          <Link key={it.testid} to={it.href} data-testid={it.testid}
                className="flex items-center gap-3 bg-[var(--paper)] rounded-md px-3 py-2.5 border border-[var(--border)] hover:border-[var(--ink)] transition group">
            <span className="w-8 h-8 rounded-full bg-[var(--accent)] flex items-center justify-center shrink-0 text-[var(--paper)]">
              {it.icon}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-[var(--ink)]">{it.count} {it.label}</div>
              <div className="text-[10px] text-[var(--ink-3)]">tap to review →</div>
            </div>
            <ArrowRight size={12} className="text-[var(--ink-3)] group-hover:text-[var(--ink)] transition"/>
          </Link>
        ))}
      </div>
    </div>
  );
}

function CardSection({ icon, title, count, href, items, render, emptyText, testid, tone = "ink" }) {
  const toneMap = {
    ink:    { bg: "bg-[var(--ink)]", txt: "text-[var(--paper)]" },
    moss:   { bg: "bg-[var(--moss)]", txt: "text-[var(--paper)]" },
    accent: { bg: "bg-[var(--accent)]", txt: "text-[var(--paper)]" },
  };
  const t = toneMap[tone];
  return (
    <div className="card p-5 flex flex-col" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className={`w-7 h-7 rounded-full ${t.bg} ${t.txt} flex items-center justify-center`}>{icon}</span>
          <span className="font-heading text-[15px]" style={{ fontWeight: 500 }}>{title}</span>
        </div>
        <Link to={href} className="text-[10px] text-[var(--ink-3)] hover:text-[var(--ink)] inline-flex items-center gap-0.5 font-medium"
              data-testid={`${testid}-view-all`}>
          view all <ArrowRight size={10}/>
        </Link>
      </div>
      <div className="flex items-baseline gap-2 mb-4">
        <div className="font-heading text-4xl tabular leading-none" style={{ fontWeight: 500 }}>{count}</div>
        <div className="text-[11px] text-[var(--ink-3)]">{count === 1 ? "open item" : "open items"}</div>
      </div>
      <div className="space-y-1.5 min-h-[60px] flex-1 border-t border-[var(--border)] pt-3">
        {items.length === 0
          ? <div className="text-[11px] text-[var(--ink-3)] italic pt-2" data-testid={`${testid}-empty`}>{emptyText}</div>
          : items.slice(0, 4).map((it) => render(it))}
      </div>
    </div>
  );
}

export default function PortalDashboard() {
  const { state } = usePortalAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API_BASE}/portal/dashboard`).then((r) => setData(r.data)).catch(console.error);
  }, []);

  if (!data) return <div className="p-12 text-[var(--ink-3)]" data-testid="portal-loading">Loading…</div>;
  const recent = data.recent || { files: [], signatures: [], invoices: [] };
  const firstName = (state?.client_user?.name || "").split(" ")[0] || "there";

  return (
    <div className="px-10 py-12 max-w-[1200px]" data-testid="portal-dashboard">
      {/* Hero */}
      <div className="mb-8">
        <div className="overline mb-2 flex items-center gap-1.5 text-[var(--accent)]">
          <Sparkles size={11}/> {greeting()}, {firstName}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl leading-tight mb-3" style={{ fontWeight: 500 }}>
          Your account with <em className="italic" style={{ color: "var(--accent)" }}>{data.org.name}</em>
        </h1>
        <p className="text-[var(--ink-2)] max-w-2xl text-base">
          Everything we&apos;re working on together — file requests, signatures and invoices — lives here.
          Come back any time.
        </p>
      </div>

      <AttentionStrip data={data} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <CardSection
          icon={<Inbox size={13}/>} title="File requests" tone="ink"
          count={data.open_file_requests}
          href="/portal/files"
          items={recent.files}
          testid="portal-card-files"
          emptyText="No documents requested. You're all caught up."
          render={(f) => (
            <Link key={f.request_id} to="/portal/files"
                  className="flex items-center justify-between gap-2 text-[12px] hover:bg-[var(--subtle)] -mx-2 px-2 py-1 rounded"
                  data-testid={`portal-files-item-${f.request_id}`}>
              <span className="truncate text-[var(--ink)]">{f.title || "Untitled"}</span>
              {f.status === "completed"
                ? <Pill tone="moss">submitted</Pill>
                : <Pill tone="accent">pending</Pill>}
            </Link>
          )}
        />

        <CardSection
          icon={<FileSignature size={13}/>} title="Signatures" tone="accent"
          count={data.pending_esigns}
          href="/portal/signatures"
          items={recent.signatures}
          testid="portal-card-signatures"
          emptyText="Nothing to sign right now."
          render={(s) => (
            <Link key={s.request_id} to="/portal/signatures"
                  className="flex items-center justify-between gap-2 text-[12px] hover:bg-[var(--subtle)] -mx-2 px-2 py-1 rounded"
                  data-testid={`portal-esign-item-${s.request_id}`}>
              <span className="truncate text-[var(--ink)]">{s.document_title || "Document"}</span>
              {s.status === "signed"
                ? <Pill tone="moss"><CheckCircle2 size={9} className="inline -mt-0.5 mr-0.5"/>signed</Pill>
                : s.status === "pending"
                  ? <Pill tone="accent"><Clock size={9} className="inline -mt-0.5 mr-0.5"/>pending</Pill>
                  : <Pill>{s.status}</Pill>}
            </Link>
          )}
        />

        <CardSection
          icon={<Receipt size={13}/>} title="Invoices" tone="moss"
          count={data.unpaid_invoices}
          href="/portal/invoices"
          items={recent.invoices}
          testid="portal-card-invoices"
          emptyText="No invoices. We'll let you know when there are."
          render={(inv) => (
            <Link key={inv.invoice_id} to={`/portal/invoices/${inv.invoice_id}`}
                  className="flex items-center justify-between gap-2 text-[12px] hover:bg-[var(--subtle)] -mx-2 px-2 py-1 rounded"
                  data-testid={`portal-invoice-item-${inv.invoice_id}`}>
              <span className="truncate text-[var(--ink)]">{inv.number || inv.invoice_id.slice(-6)}</span>
              <span className="font-mono text-[11px] tabular text-[var(--ink-2)]">{fmtAmt(inv.total)}</span>
              {inv.status === "paid"
                ? <Pill tone="moss">paid</Pill>
                : inv.status === "sent"
                  ? <Pill tone="accent">due {fmtDate(inv.due_at)}</Pill>
                  : <Pill>{inv.status}</Pill>}
            </Link>
          )}
        />
      </div>

      {/* Firm signature block */}
      <div className="rounded-md border border-[var(--border)] p-6 flex items-start gap-4"
           style={{ background: "var(--paper)" }}
           data-testid="portal-firm-card">
        <div className="w-12 h-12 rounded-full bg-[var(--ink)] text-[var(--paper)] flex items-center justify-center font-heading text-xl shrink-0"
             style={{ fontWeight: 500 }}>
          {(data.org.name || "").slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline text-[10px] mb-1">Your accounting team</div>
          <div className="font-heading text-xl leading-tight" style={{ fontWeight: 500 }}>{data.org.name}</div>
          <div className="text-[var(--ink-3)] text-[12px] mt-0.5">
            Account: <span className="font-mono text-[var(--ink-2)]">{data.client.name}</span>
          </div>
          <div className="text-[11px] text-[var(--ink-3)] mt-2 max-w-lg">
            Questions? Reply to any email from us — messages route straight to your account manager.
          </div>
        </div>
      </div>
    </div>
  );
}
